import * as Dialog from '@radix-ui/react-dialog';
import dynamic from 'next/dynamic';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import React, { useEffect } from 'react';
import {
  StyledAlertDialogOverlay,
  StyledAlertDialogContent,
} from '@/shared/ui/alert-dialog/styles';
import { useSearchParams } from 'next/navigation';
import {
  useUserPhone,
  useUserPhoneConfirm,
  useUserEmail,
  useUserEmailConfirm,
  useUserTelegram,
} from '@/shared/providers/user-provider/use-user';
import { useConfirmDataPrompt } from '@/entities/data-confirmation/model/use-confirm-data-prompt';
import { brandIsLm } from '@/shared/config/brand-by-target.mjs';
import { useIsLoginConfirmationDone } from '@/features/login-confirmation-modal/model/hook';
import { createDialogOpenChangeHandler } from '@/shared/libs/dialog-open-change';

const ModalPromptsContent = dynamic(
  () => import('./modal-prompts-content').then((m) => m.ModalPromptsContent),
  { ssr: false }
);

const ModalPrompts = () => {
  const {
    showPopup,
    onPopupShow,
    onPopupClose,
    addQueryParam,
    role,
    removeQueryParam,
  } = useConfirmDataPrompt();

  const phone = useUserPhone();
  const phoneConfirm = useUserPhoneConfirm();
  const email = useUserEmail();
  const emailConfirm = useUserEmailConfirm();
  const telegram = useUserTelegram();
  const searchParams = useSearchParams();
  const isConfirmData = searchParams.get('modal') === 'confirm';
  const triggerFromProfileParam = searchParams.get('triggerFromProfile');
  const confirmationCodeParam = searchParams.get('confirmationCode');

  const isLoginConfirmationDone = useIsLoginConfirmationDone();

  const isPhonePending = Boolean(phone) && !phoneConfirm && !brandIsLm;
  const isEmailPending = Boolean(email) && !emailConfirm;
  const hasPendingConfirmation = isEmailPending || isPhonePending;
  const isConfirmationFromProfile = Boolean(
    triggerFromProfileParam && confirmationCodeParam
  );

  useEffect(() => {
    if (
      hasPendingConfirmation &&
      isLoginConfirmationDone &&
      (showPopup || isConfirmData) &&
      role !== 'player' &&
      !telegram
    ) {
      onPopupShow();
    }
  }, []);

  useEffect(() => {
    if (isConfirmationFromProfile) onPopupClose();
  }, [isConfirmationFromProfile]);

  const isShowModal =
    !isConfirmationFromProfile &&
    hasPendingConfirmation &&
    !telegram &&
    isLoginConfirmationDone &&
    isConfirmData;

  useEffect(() => {
    if (isConfirmData && (!hasPendingConfirmation || telegram)) {
      removeQueryParam('modal');
    }
  }, [isConfirmData, hasPendingConfirmation, telegram]);

  return (
    <Dialog.Root
      open={isShowModal}
      onOpenChange={createDialogOpenChangeHandler(onPopupShow, onPopupClose)}
    >
      <Dialog.Portal>
        <VisuallyHidden>
          <Dialog.Title>DataManagement</Dialog.Title>
        </VisuallyHidden>
        <StyledAlertDialogOverlay onClick={onPopupClose} />
        <StyledAlertDialogContent aria-describedby="" data-item="confirm_data">
          <ModalPromptsContent
            handleClose={onPopupClose}
            addQueryParam={addQueryParam}
            isEmailPending={isEmailPending}
            isPhonePending={isPhonePending}
          />
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { ModalPrompts };
